'use client';

import Image from 'next/image';
import Link from 'next/link';
import { VariantProps } from 'tailwind-variants';

import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { CategoryButton } from '../types';

import { styles } from './UiCategoryButtonBase.styles';

type Variants = VariantProps<typeof styles>;

export interface UiCategoryButtonBaseProps {
  item: CategoryButton;
  size?: Variants['size'];
  mobileSize?: Variants['mobileSize'];
  isGradient?: boolean;
  nameColor?: string;
  withoutImageBorder?: boolean;
  onClick?: () => void;
  className?: string;
}

export function UiCategoryButtonBase({
  item,
  size,
  isGradient = false,
  nameColor,
  withoutImageBorder = false,
  onClick,
  className,
  mobileSize
}: UiCategoryButtonBaseProps) {
  const slots = styles({ size, active: item.active, mobileSize });

  const shouldUseGradient = isGradient || Boolean(item.gradient);

  const isMobile = useMediaQuery('md', 'max');

  const content = (
    <>
      <div className={slots.itemImageWrapper()}>
        <div
          className={slots.itemImageBorder()}
          style={{
            background: shouldUseGradient
              ? (item.gradient ??
                (item.color
                  ? `linear-gradient(90deg, #fff 12%, ${item.color} 45%)`
                  : undefined))
              : item.color
          }}
        />
        <Image
          className={slots.itemImage({
            className: item.color ? 'border-white' : 'border-bg-page',
            withoutImageBorder
          })}
          alt={item.alt || 'icon'}
          width="92"
          height="92"
          src={item.image}
        />
      </div>
      <div className={slots.itemText()}>
        {item.name && (
          <p
            className={slots.itemName()}
            style={{ color: nameColor }}
          >
            {item.name}
          </p>
        )}

        {item.title && (
          <p
            dangerouslySetInnerHTML={{
              __html:
                isMobile && item.mobileTitle ? item.mobileTitle : item.title
            }}
            className={slots.itemTitle()}
          />
        )}
      </div>
    </>
  );

  if (item.link) {
    return (
      <Link
        key={item.id}
        href={item.link}
        className={slots.base({ class: className })}
        target={item.target ? '_blank' : '_self'}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }
  return (
    <div
      key={item.id}
      role="button"
      className={slots.base({ class: className })}
      onClick={onClick}
    >
      {content}
    </div>
  );
}
